import { ViewportScroller, NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { Component, HostListener, OnInit, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

//* Components
import { RewardModalComponent } from './reward-modal/reward-modal.component';
import { RedeemComponent } from '../purchase/redeem/redeem.component';
import { AddCardComponent } from './add-card/add-card.component';
import { SigninComponent } from '../shared/components/signin/signin.component';

//* 3rd
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ReCaptchaV3Service } from 'ng-recaptcha';

//* Material
import { MatDialog } from '@angular/material/dialog';

//* Models
import { Reward } from '@models/awards.model';
import { ZonesData } from '@models/zones.model';

//* Services
import { ClubService } from '@services/club.service';
import { SnackbarService } from '@services/snackbar.service';
import { SeoService } from '@services/seo.service';
import { AuthService } from '@services/auth.service';

//* Shared
import { TAGS_CLUB } from '@shared/seo.tags';
import { NUMBER_REGEX } from '@shared/utils';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatRippleModule, MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LocalstorageService } from '@services/localstorage.service';
import { AppStateUser } from '../state/user/user.reducer';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-club',
  templateUrl: './club.component.html',
  styleUrls: ['./club.component.scss'],
  standalone: true,
  imports: [LottieComponent, MatFormFieldModule, FormsModule, ReactiveFormsModule, MatInputModule, MatButtonModule, MatRippleModule, MatSelectModule, MatOptionModule, MatIconModule, MatProgressSpinnerModule, TranslateModule, NgOptimizedImage]
})
export class ClubComponent implements OnInit, OnDestroy {


  public loading = false;
  public mobile = false;

  private subs = new Subject<void>();
  public awardsList: Reward[] = [];
  public zonesList: ZonesData[] = [];
  public token = this.localstorageService.getProperty('token');
  public selectedZone = new UntypedFormControl('LAGUNA');
  public currentPage = 1;
  public totalPages: number;
  public pagination = 10;
  public prev: boolean;
  public next: boolean;
  public alsuperPoints: any;
  public alsuperPointsLoading = false;
  public showClubVerifyError = false;
  public clientData: any;
  public hasMembership = false;
  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (isPlatformBrowser(this.platformId)) this.mobile = window.innerWidth <= 1024;
  }

  public checkClubForm = new UntypedFormGroup({
    clubCardID: new UntypedFormControl('', [Validators.required, Validators.pattern(NUMBER_REGEX), Validators.minLength(12)]),
    clubCardNumber: new UntypedFormControl('', [Validators.required, Validators.pattern(NUMBER_REGEX), Validators.minLength(10)])
  });

  public options: AnimationOptions = {
    path: '/assets/animations/loader.json',
  };

  constructor(
    private dialog: MatDialog,
    private clubService: ClubService,
    private snackbarService: SnackbarService,
    private scroll: ViewportScroller,
    private seoService: SeoService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private recaptchaV3Service: ReCaptchaV3Service,
    private authService: AuthService,
    private localstorageService: LocalstorageService,
    private storeUser: Store<AppStateUser>,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    if (isPlatformBrowser(this.platformId)) this.mobile = window.innerWidth <= 1024;
  }

  ngOnInit(): void {
    this.setSEOSettings();
    this.scrollTop();
    this.getZones();
    this.getClientData();
    this.selectedZone.valueChanges.pipe(takeUntil(this.subs)).subscribe((res: string) => {
      const clean = res.split(' ').join('-');
      this.router.navigate(['club-alsuper'], { queryParams: { zona: clean }, queryParamsHandling: 'merge' });
      this.getRewards(res);
    });
  }


  public getClientData(): void {
    this.alsuperPointsLoading = true;
    this.storeUser.select('user').pipe(takeUntil(this.subs)).subscribe(({ data }) => {
      this.clientData = data;
      this.hasMembership = this.clientData?.membership ? true : false;
      this.alsuperPointsLoading = false;
    });

  }


  ngOnDestroy(): void {
    this.subs.next();
    this.subs.complete();
  }

  private setSEOSettings(): void {
    this.seoService.updateTitle(TAGS_CLUB.title);
    this.seoService.updateDescription(TAGS_CLUB.description);
    if (isPlatformBrowser(this.platformId)) {
      this.seoService.createLinkForCanonicalURL(window.location.href);
      this.seoService.updateUrl(window.location.href);
    }
  }

  private scrollTop(): void {
    this.scroll.scrollToPosition([0, 0]);
  }

  private async getZones(): Promise<void> {
    this.currentPage = 1;
    try {
      const { data } = await this.clubService.getZones();
      this.zonesList = data;
      const zone = this.activatedRoute.snapshot.queryParams.zona;
      if (zone === undefined) {
        this.router.navigate(['club-alsuper'], { queryParams: { zona: 'LAGUNA' }, queryParamsHandling: 'merge' });
      } else {
        const clean = zone.split('-').join(' ');
        this.selectedZone.patchValue(clean);
      }
      await this.getRewards(this.selectedZone.value);
    } catch (error) { }
  }

  private async getRewards(zone: string): Promise<void> {
    try {
      const { data } = await this.clubService.getRewards(zone, this.currentPage, this.pagination);
      this.awardsList = data.rewards;
      this.totalPages = data.total / this.pagination;
      if (!Number.isInteger(this.totalPages)) {
        this.totalPages = Math.ceil(this.totalPages);
      }
      this.setActiveBtn();
      this.loading = false;
    } catch (error) { }
  }

  /* PAGINATE */
  public setActiveBtn(): void {
    if (this.currentPage === this.totalPages && this.totalPages > 1) {
      this.prev = true;
      this.next = false;
    } else if (this.currentPage === this.totalPages && this.totalPages === 1) {
      this.prev = false;
      this.next = false;
    } else if (this.currentPage < this.totalPages && this.currentPage === 1) {
      this.prev = false;
      this.next = true;
    } else if (this.currentPage < this.totalPages && this.currentPage > 1) {
      this.prev = true;
      this.next = true;
    }
  }
  /* PAGINATE */

  /* PAGINATE BUTTONS */
  public backPage(): void {
    if (this.currentPage === 1) return;
    this.currentPage--;
    this.getRewards(this.selectedZone.value);
  }

  public nextPage(): void {
    if (this.currentPage === this.totalPages) return;
    this.currentPage++;
    this.getRewards(this.selectedZone.value);
  }
  /* PAGINATE BUTTONS */

  public onSelectingZone(zone: any): void {
    this.loading = true;
    this.selectedZone.setValue(zone.zone);
    this.currentPage = 1;
    this.getRewards(zone.zone);
  }

  public async onCheckCardPoints(): Promise<any> {
    this.recaptchaV3Service.execute('register')
      .pipe(takeUntil(this.subs))
      .subscribe(async (token: any) => {
        if (token) {
          const { data } = await this.authService.verifyReCaptcha(token);
          if (data?.message === 'Token verified successfully') {
            this.showClubVerifyError = false;
            this.loading = true;
            try {
              const { data } = await this.clubService.getClubPointsByNumber(this.checkClubForm.get('clubCardID')?.value, this.checkClubForm.get('clubCardNumber')?.value);
              this.loading = false;
              this.dialog.open(RedeemComponent, {
                autoFocus: false,
                data: {
                  card_id: this.checkClubForm.get('clubCardID')?.value,
                  card_data: data.membership_data[0],
                  isPointsCheck: true
                }
              });
            } catch (error: any) {
              this.loading = false;
              error?.error?.data?.membership_data.message === 'Not same number' ? this.showClubVerifyError = true
                : this.snackbarService.openSnackbar('GENERAL.error', 'warn');
            }
          }
        }
      });
  }


  public openRewardModal(reward: any): void {
    this.dialog.open(RewardModalComponent, {
      width: '384px',
      data: reward
    });
  }

  public async goToActivate(): Promise<any> {
    this.router.navigate(['/activar']).then(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.scroll.scrollToPosition([0, 0]);
      }
    });
  }

  public reduceText(text: string): string {
    if (text.length >= 60) {
      return `${text.substring(0, 60)}...`;
    } else {
      return text;
    }
  }

  public async addCard(): Promise<void> {
    if (this.token) {
      this.dialog.open(AddCardComponent, {
        panelClass: 'as-activate-club',
        data: { isUpdate: false }
      }).afterClosed().subscribe(response => {
        if (response) {
          this.snackbarService.openSnackbar('CLUB.cardAddedSuccess', 'success');
        }
      });
    } else {
      this.dialog.open(SigninComponent, { autoFocus: false });
    }
  }
}
